/**
 * Hex Pathfinding Utilities
 * 
 * Implements BFS pathfinding for hex grids to find shortest routes between hexes.
 * Avoids ocean hexes and finds paths based on pure distance (no terrain weighting).
 */

import { AxialCoord, TerrainMap, TerrainType } from '../types/terrain';
import { hexNeighbors, hexDistance, hexKey } from './hexMath';

/**
 * Find the shortest path between two hexes using BFS
 * Returns array of hex coordinates forming the path, or null if no path exists
 * The path includes both start and destination hexes
 * 
 * @param start - Starting hex coordinate
 * @param destination - Destination hex coordinate
 * @param map - Terrain map to check for obstacles
 * @returns Array of coordinates forming the path, or null if no path exists
 */
export function findShortestPath(
  start: AxialCoord,
  destination: AxialCoord,
  map: TerrainMap
): AxialCoord[] | null {
  // Check if start and destination are the same
  if (start.q === destination.q && start.r === destination.r) {
    return [start];
  }

  // Check if destination is valid (not ocean, in bounds)
  const destKey = hexKey(destination);
  const destHex = map.hexes.get(destKey);
  if (!destHex || destHex.terrain === TerrainType.Ocean) {
    return null;
  }

  // BFS data structures
  const queue: AxialCoord[] = [start];
  const visited = new Set<string>([hexKey(start)]);
  const cameFrom = new Map<string, AxialCoord>();

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentKey = hexKey(current);

    // Check if we reached the destination
    if (current.q === destination.q && current.r === destination.r) {
      // Reconstruct path
      return reconstructPath(cameFrom, start, destination);
    }

    // Explore neighbors
    const neighbors = hexNeighbors(current);
    for (const neighbor of neighbors) {
      const neighborKey = hexKey(neighbor);
      
      // Skip if already visited
      if (visited.has(neighborKey)) {
        continue;
      }

      // Check if neighbor is valid (exists and not ocean)
      const neighborHex = map.hexes.get(neighborKey);
      if (!neighborHex || neighborHex.terrain === TerrainType.Ocean) {
        continue;
      }

      // Mark as visited and add to queue
      visited.add(neighborKey);
      cameFrom.set(neighborKey, current);
      queue.push(neighbor);
    }
  }

  // No path found
  return null;
}

/**
 * Reconstruct the path from the cameFrom map
 */
function reconstructPath(
  cameFrom: Map<string, AxialCoord>,
  start: AxialCoord,
  destination: AxialCoord
): AxialCoord[] {
  const path: AxialCoord[] = [destination];
  let current = destination;

  while (current.q !== start.q || current.r !== start.r) {
    const currentKey = hexKey(current);
    const previous = cameFrom.get(currentKey);
    if (!previous) {
      // This should never happen if BFS worked correctly
      console.error('Path reconstruction failed - missing previous node');
      return [start, destination];
    }
    path.unshift(previous);
    current = previous;
  }

  return path;
}

/**
 * Calculate the total stamina cost for a route
 * Cost is 1 stamina per hex moved (not counting starting hex)
 */
export function calculateStaminaCost(route: AxialCoord[]): number {
  // Starting hex doesn't cost stamina, only movement hexes do
  return Math.max(0, route.length - 1);
}

/**
 * Calculate the total travel time for a route
 * @param route - Array of hex coordinates
 * @param singleHexTravelTime - Time in ms to travel one hex
 */
export function calculateTravelTime(route: AxialCoord[], singleHexTravelTime: number): number {
  // Starting hex doesn't take time, only movement hexes do
  const movementHexes = Math.max(0, route.length - 1);
  return movementHexes * singleHexTravelTime;
}

/**
 * Check if a direct line path between two hexes is valid (no obstacles)
 * This can be used as an optimization before running full BFS
 */
export function canTravelDirectly(
  start: AxialCoord,
  destination: AxialCoord,
  map: TerrainMap
): boolean {
  const distance = hexDistance(start, destination);
  
  // For adjacent hexes, just check if destination is valid
  if (distance <= 1) {
    const destKey = hexKey(destination);
    const destHex = map.hexes.get(destKey);
    return destHex !== undefined && destHex.terrain !== TerrainType.Ocean;
  }

  // For longer distances, we'd need to check the line, but BFS is more reliable
  // So we'll just return false and let BFS handle it
  return false;
}

